import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  TableContainer,
  Table,
  TableBody
} from '@material-ui/core';
import {getId, getFieldValue, adaptCameraStatusValue} from '../../helpers';
import CTableToolbar from './ctable-toolbar';
import CTableHead from './ctable-head';
import CTableFilter from './ctable-filter';
import CTableRow from './ctable-row';
import CTableFooter from './ctable-footer';

const defaultRowPerPage = 20;

function CTable (props) {
  const { selectedRows = [], noOfNonSelectableRows = 0, colDef = [], onRowSelect, onExpand } = props;
  const [rowsPerPage, setRowsPerpage] = useState(defaultRowPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState(-1);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState(colDef[0].field);
  const filteredData = useMemo(applyFilter, [colDef, props.data, sortOrder, sortBy]);
  // const [data, setData] = useState(filteredData);
  const redirectId = props.redirect?.id;
  const filteredDataLength = filteredData.length;

  const onChangePage = useCallback((_, newPage) => {
    setCurrentPage(newPage);
  }, []);

  const onChangeRowsPerPage = useCallback((e) => {
    setRowsPerpage(parseInt(e.target.value));
    setCurrentPage(0);
  }, []);

  const onExpandedRow = useCallback((rowData) => {
    if(props.updateRedirect !== undefined)
      props.updateRedirect({});
    setExpandedRow(getId(rowData));
  }, []);

  const onSortChange = useCallback(newSortBy => {
    const isAsc = sortBy === newSortBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newSortBy);
  }, [sortBy, sortOrder]);

  const selectedRowCount = selectedRows.length || 0;
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pagedData = getPagedData();
  const isExpandable = !!onExpand; // to avoid render of filter and head component

  function getPagedData() {
    return redirectId !== undefined
      ? findDataFromRedirect()
      : filteredData.slice(startIndex, endIndex);
  }

  function findDataFromRedirect() {
    let pagedData;
    let isNotIncluded = true;
    let start = startIndex, end = endIndex;
    while (isNotIncluded) {
      if(end <= filteredDataLength)
        pagedData = filteredData.slice(start, end);
      else {
        if(start < filteredDataLength)
          pagedData = filteredData.slice(start, filteredDataLength);
        else if(start === filteredDataLength)
          pagedData = filteredData.slice(start);
      }
      let idList = pagedData.map(rowData => getId(rowData));
      if(!idList.includes(redirectId)) {
        let newPage = currentPage + 1;
        start = newPage * rowsPerPage;
        end = start + rowsPerPage;
        setCurrentPage(newPage);
      }
      else
        isNotIncluded = false;
    }
    return pagedData;
  }

  function descendingComparator(val1, val2) {
    if (val2[sortBy] < val1[sortBy]) {
      return -1;
    }
    if (val2[sortBy] > val1[sortBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator() {
    return sortOrder === 'desc'
      ? (val1, val2) => descendingComparator(val1, val2)
      : (val1, val2) => -descendingComparator(val1, val2);
  }

  function applyFilter () {
    if(props.redirect !== undefined && props.redirect !== null) {
      const {filterColumn = '', filterValue = ''} = props.redirect;
      if (filterColumn != null && filterColumn !== '' && filterValue != null && filterValue !== '') {
        colDef?.filter(col => col.id === filterColumn)?.forEach(col => col.filter = filterValue);
        if (props.updateRedirect !== undefined)
          props.updateRedirect({});
      }
    }
    const col = colDef.filter(c => !c.hidden && !c.filterByParent && c.filter);
    let fData = props.data;
    if (col) {
      col
        .forEach(c => {
          const { filterType, field, filter, getValue } = c;
          fData = Array.from(fData).filter(fd => {
            if (filterType === 'none') {
              return true;
            } else if ((filterType === 'daterange') || (filterType === 'heartbeat')) { // need to implement
              return true;
            } else if (filterType === 'select') {
              return (filter === 'None') || (filter === 'all') || (fd[field] === filter) ||
                (getValue && (getValue(fd) === filter)) ||
                (typeof fd[field] === 'number' && adaptCameraStatusValue(fd[field]) === filter);
            }
            return getFieldValue(c, fd).toLowerCase().includes(filter.toLowerCase());
          });
        });
    }
    fData.sort((val1, val2) => {
      return getComparator()(val1, val2);
    });
    return fData;
  }

  function tableFooter () {
    const childProps = { count: filteredData.length, rowsPerPage, currentPage, onChangePage, onChangeRowsPerPage };
    return (
      <CTableFooter {...childProps} />
    );
  }

  function tableRows () {
    return pagedData.map((rowData, idx) => {
      const key = getId(rowData);
      const isSelected = !!selectedRows.find(f => getId(f) === key);
      const isExpanded = getIsExpanded(key);
      const childProps = { idx, isSelected, noOfNonSelectableRows, colDef, rowData, isExpandable, isExpanded, onRowSelect,
        onRowClicked: props.onRowClicked, onExpandedRow, onExpand, onPublicKeyInfo: props.onPublicKeyInfo };
      return (
        <CTableRow key={key} {...childProps} />
      );
    });
  }

  function getIsExpanded(key) {
    return redirectId === key ? true : expandedRow === key;
  }

  function tableFilter () {
    const childProps = { colDef, onFilterChange: props.onFilterChange, onRowSelect, isExpandable };
    return (<CTableFilter {...childProps} />);
  }

  function tableHead () {
    const childProps = { selectedRows, noOfNonSelectableRows, pagedData, colDef, isExpandable, sortOrder, sortBy,
      onAllRowSelect: props.onAllRowSelect,  onSortChange };
    return (
      <CTableHead {...childProps} />
    );
  }

  function tableToolBar () {
    return props.toolbar !== undefined ? props.toolbar() : defaultToolbar();
  }

  function defaultToolbar() {
    const childProps = { selectedRowCount, colDef, leftSideToolbarActions: props.leftSideToolbarActions,
      onColumnChange: props.onColumnChange, onDelete: props.onDelete, onSetDefault: props.onSetDefault, onRestart: props.onRestart };
    return (<CTableToolbar {...childProps} />);
  }

  return (
    <Paper>
      {tableToolBar()}
      <TableContainer style={{ padding: '0px 24px' }}>
        <Table>
          {tableHead()}
          <TableBody>
            {tableFilter()}
            {tableRows()}
          </TableBody>
          {tableFooter()}
        </Table>
      </TableContainer>
    </Paper>
  );
}

CTable.propTypes = {
  colDef: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  leftSideToolbarActions: PropTypes.func.isRequired,
  noOfNonSelectableRows: PropTypes.number.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onRowSelect: PropTypes.func,
  onColumnChange: PropTypes.func,
  onRowClicked: PropTypes.func,
  onAllRowSelect: PropTypes.func,
  onDelete: PropTypes.func,
  onSetDefault: PropTypes.func,
  onRestart: PropTypes.func,
  onExpand: PropTypes.func,
  onPublicKeyInfo: PropTypes.func,
  updateRedirect: PropTypes.func
};

export default memo(CTable);
